<script setup>
import { computed } from 'vue'
import markGradient from '@/assets/brand/logo_mark_gradient.png'
import markGradient2x from '@/assets/brand/logo_mark_gradient@2x.png'
import markWhite from '@/assets/brand/logo_mark_white.png'
import markWhite2x from '@/assets/brand/logo_mark_white@2x.png'

/**
 * BrandLogo — le lockup Bifrost : pictogramme (l'arche-pont, ses pylônes,
 * l'étoile) + wordmark.
 *
 * Le pictogramme est un PNG (1x/2x) en deux variantes — `gradient` sur fond
 * clair, `white` sur fond sombre — choisies via la prop `variant`. Le
 * wordmark reste du vrai texte HTML (Space Grotesk via font-heading) qui
 * hérite de `currentColor`. Le point final garde son magenta.
 *
 * `shine` (défaut true) : une lueur balaie périodiquement le pictogramme —
 * le PNG sert de `mask-image` à un calque de lumière, la barre lumineuse
 * n'existe donc QUE dans la forme du logo — et une flare dorée s'allume sur
 * l'étoile au passage. Désactivé sous prefers-reduced-motion.
 *
 * `size` (px) fixe la hauteur du pictogramme et met le wordmark à l'échelle ;
 * `wordmark: false` pour le pictogramme seul.
 */
const props = defineProps({
  size: { type: Number, default: 32 },
  wordmark: { type: Boolean, default: true },
  shine: { type: Boolean, default: true },
  variant: {
    type: String,
    default: 'gradient',
    validator: (value) => ['gradient', 'white'].includes(value)
  }
})

const src = computed(() => (props.variant === 'white' ? markWhite : markGradient))
const srcset = computed(() =>
  props.variant === 'white'
    ? `${markWhite} 1x, ${markWhite2x} 2x`
    : `${markGradient} 1x, ${markGradient2x} 2x`
)

// Le calque de lueur est découpé par la forme du logo : le PNG (alpha)
// devient le masque du conteneur qui porte la barre lumineuse.
const shineMask = computed(() => ({
  maskImage: `url(${src.value})`,
  maskRepeat: 'no-repeat',
  maskSize: '100% 100%',
  WebkitMaskImage: `url(${src.value})`,
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskSize: '100% 100%'
}))
</script>

<template>
  <span class="inline-flex items-center select-none" :style="{ gap: `${props.size * 0.32}px` }">
    <span class="relative inline-block shrink-0">
      <img
        :src="src"
        :srcset="srcset"
        alt=""
        aria-hidden="true"
        class="block w-auto select-none"
        :style="{ height: `${props.size}px` }"
        draggable="false"
      />

      <template v-if="props.shine">
        <!-- Balayage lumineux, clippé à la forme du logo par le mask -->
        <span class="bf-shine absolute inset-0 overflow-hidden" :style="shineMask" aria-hidden="true">
          <span class="bf-shine__bar"></span>
        </span>
        <!-- Flare sur l'étoile (position relative dans le visuel : ~59% / 10%) -->
        <span class="bf-flare absolute" aria-hidden="true"></span>
      </template>
    </span>

    <span
      v-if="props.wordmark"
      class="font-heading font-semibold tracking-tight leading-none"
      :style="{ fontSize: `${props.size * 0.78}px` }"
    >Bifrost<span class="text-[#d75fe8]">.</span></span>
  </span>
</template>

<style scoped>
/* ---- Balayage : une barre de lumière inclinée traverse le pictogramme.
   Cycle 6s : le sweep dure ~1.1s puis longue pause (les % de keyframes
   encodent la pause — pas de JS). ---- */
.bf-shine__bar {
  position: absolute;
  top: -20%;
  bottom: -20%;
  width: 34%;
  transform: translateX(-160%) skewX(-18deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.85) 50%,
    transparent
  );
  animation: bf-sweep 6s ease-in-out infinite;
}

@keyframes bf-sweep {
  0% {
    transform: translateX(-160%) skewX(-18deg);
  }
  18% {
    transform: translateX(460%) skewX(-18deg);
  }
  100% {
    transform: translateX(460%) skewX(-18deg);
  }
}

/* ---- Flare de l'étoile : s'embrase quand le balayage la traverse
   (même horloge 6s, pic calé sur le passage de la barre à ~60% de la
   largeur, soit ~11% du cycle). Deux halos : cœur blanc + halo doré. ---- */
.bf-flare {
  left: 59%;
  top: 10%;
  width: 38%;
  aspect-ratio: 1;
  max-width: 3.5rem;
  transform: translate(-50%, -50%) scale(0.4);
  border-radius: 9999px;
  opacity: 0;
  background:
    radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, transparent 22%),
    radial-gradient(circle, rgba(255, 217, 162, 0.8) 8%, rgba(255, 158, 77, 0.35) 34%, transparent 62%);
  filter: blur(0.5px);
  animation: bf-flare 6s ease-in-out infinite;
  pointer-events: none;
}

@keyframes bf-flare {
  0%,
  6% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.4) rotate(0deg);
  }
  11% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.15) rotate(20deg);
  }
  17% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.55) rotate(35deg);
  }
  100% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bf-shine__bar,
  .bf-flare {
    animation: none;
    opacity: 0;
  }
}
</style>
